import classNames from "classnames"
import type {
  ComponentProps,
  Ref,
  MouseEvent as ReactMouseEvent,
} from "react"

import {
  forwardRef,
  useCallback,
} from "react"

import styles from "./Modal.module.scss"
import { useModalContext } from "./useModalContext"
import { ActionButton } from "src/buttons"

type Props = Omit<ComponentProps<typeof ActionButton>, "ref">

/*
 * this component renders a button (using our button component) that closes the
 * modal after its on click handler is executed
 *
 * the modal will not close if the on click handler throws an error
 */
export const ModalButton = forwardRef<HTMLButtonElement, Props>(
  function ModalButton(props: Props, propRef: Ref<HTMLButtonElement>) {
    const {
      children,
      onClick,
      ...restProps
    } = props

    const { setOpen } = useModalContext()

    const closeOnClick = useCallback(
      (mouseEvent: ReactMouseEvent<HTMLButtonElement>) => {
        const asyncCallback = async () => {
          try {
            if (onClick) {
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await onClick(mouseEvent)
            }

            // if the onClick function didn't throw an error then close the modal
            setOpen(false)
          } catch (e) {
            console.warn(e)
            throw e
          }
        }

        void asyncCallback()
      },
      [
        onClick,
        setOpen,
      ],
    )

    const className = classNames(
      styles.modalButton,
      restProps.className,
    )

    return (
      <ActionButton
        {...restProps}
        className={className}
        onClick={closeOnClick}
        ref={propRef}
      >
        {children}
      </ActionButton>
    )
  },
)
